import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  dreamTeamAsPickLight,
  getEventDreamTeam,
  getEventDreamTeamFormation,
  getOverallDreamTeam,
  getOverallDreamTeamFormation,
} from "core-integration/src/store/dream-teams/reducers";
import {
  fetchEventDreamTeam,
  fetchOverallDreamTeam,
} from "core-integration/src/store/dream-teams/thunks";
import {
  getElementsById,
  getElementsEventDataById,
} from "core-integration/src/store/elements/reducers";
import {
  fetchEventLive,
  showElementSummary,
} from "core-integration/src/store/elements/thunks";
import { IPickLight } from "core-integration/src/store/entries/types";
import {
  getCurrentEvent,
  getEventsById,
} from "core-integration/src/store/events/reducers";
import { getFixturesForEventById } from "core-integration/src/store/fixtures/reducers";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { useEffect, useState } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import styled from "styled-components";
import BoldLink from "../BoldLink";
import BoldLinkButton from "../BoldLinkButton";
import Box from "../Box";
import ButtonLink from "../ButtonLink";
import ElementShirt from "../ElementShirt";
import Fixtures from "../Fixtures";
import { ControlArrowLeft, ControlArrowRight } from "../icons/Arrows";
import { Main, Secondary, Wrapper } from "../Layout";
import Media from "../Media";
import {
  EventPager,
  PagerButton,
  PagerButtonNext,
  PagerHeading,
} from "../Pager";
import { NotFound } from "../Routes";
import SubHeading from "../SubHeading";
import TabPanel from "../tabs/TabPanel";
import Tabs from "../tabs/Tabs";
import Title from "../Title";
import DreamTeamTable from "./DreamTeamTable";
import ElementExplainDialog from "./ElementExplainDialog";
import PitchFormation from "./PitchFormation";
import valueForPlayerElement from "./valueForPlayedElement";

const DreamTeamTableWrap = styled.div`
  margin: ${({ theme }) => theme.space[4]} 0;
  background-color: isWhiteSpaceLike;
`;

const DTScoreboardWrapper = styled.div`
  margin: ${({ theme }) => theme.space[4]} 0;
  padding: ${({ theme }) => theme.space[2]};
  border: ${({ theme }) => theme.borders[2]};
  border-radius: ${({ theme }) => theme.radii[2]};
`;

const DTScoreboard = styled.div`
  display: flex;
`;

const DTScoreboardPanel = styled.div`
  flex: 1;
  margin: ${({ theme }) => theme.space[2]};
  padding: ${({ theme }) => theme.space[2]};
  border: ${({ theme }) => theme.borders[3]};
  border-radius: ${({ theme }) => theme.radii[2]};
  background-color: white;
  text-align: center;
`;

const DTScoreboardHeading = styled.h3`
  margin: ${({ theme }) => theme.space[1]};
  font-size: ${({ theme }) => theme.fontSizes[4]};
`;

const DTScoreboardValue = styled.div`
  position: relative;
  color: ${({ theme }) => theme.colors.secondaryThree};
  font-size: 4rem;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    padding-top: 0.6rem;
    padding-bottom: 1rem;
  }
`;

const TopPlayer = styled.div`
  display: inline-block;
`;

const TopPlayerData = styled.div`
  text-align: start;
`;

const PitchWrapper = styled(Box)`
  border: ${({ theme }) => theme.borders[2]};
  border-radius: ${({ theme }) => theme.radii[2]};
  background-color: ${({ theme }) => theme.colors.greyLight3};
  padding-top: ${({ theme }) => theme.space[4]};
`;

type Props = WithTranslation;

const DreamTeam: React.FC<Props> = ({ t }) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const { eventId } = useParams();
  const [elementForMenu, setElementForMenu] = useState<number>(0);

  const data = useSelector((state: RootState) =>
    eventId
      ? getEventDreamTeam(state, Number(eventId))
      : getOverallDreamTeam(state),
  );

  const elementsById = useSelector(getElementsById);
  const elementsDataById = useSelector((state: RootState) =>
    getElementsEventDataById(state, Number(eventId)),
  );
  const event = useSelector(getEventsById)[Number(eventId)];
  const fixturesById = useSelector((state: RootState) =>
    getFixturesForEventById(state, Number(eventId)),
  );
  const formation = useSelector((state: RootState) =>
    eventId
      ? getEventDreamTeamFormation(state, Number(eventId))
      : getOverallDreamTeamFormation(state),
  );
  const now = useSelector(getCurrentEvent);
  const teamsById = useSelector(getTeamsById);

  const showElementDialog = (elementId: number) =>
    dispatch(showElementSummary(elementId));

  const handleShowMenuForElement = (element: number) => {
    setElementForMenu(element);
  };

  const handleHideMenuForElement = () => {
    setElementForMenu(0);
  };

  useEffect(() => {
    if (event) {
      dispatch(fetchEventDreamTeam(event.id));
      dispatch(fetchEventLive(event.id));
    } else {
      dispatch(fetchOverallDreamTeam());
    }
  }, [dispatch, event]);

  if (!now) {
    return <NotFound />;
  }

  if (!data) {
    return null;
  }
  const picks = dreamTeamAsPickLight(data.team);
  const topPlayer = elementsById[data.top_player.id];

  // For the overall team we just show the standard element dialog as there
  // is no explain
  if (elementForMenu && !event) {
    showElementDialog(elementForMenu);
  }

  // Create a new function on each render as data could have changed and
  // need to ensure a render of connected subcomponents
  const renderPickValue = event
    ? valueForPlayerElement({
        elementsById,
        elementsDataById,
        fixturesById,
        teamsById,
      })
    : (pick: IPickLight) => {
        const elementData = data.team.filter((e) => e.element === pick.element);
        return elementData.length ? elementData[0].points : null;
      };
  return (
    <Wrapper>
      <Main>
        <Box mx={2}>
          <Title>{t("dreamTeam.dt", "Dream Team")}</Title>
        </Box>
        {/* ROUND PAGER */}
        <DTScoreboardWrapper>
          {event ? (
            <Box my={2}>
              <PagerHeading>{event.name}</PagerHeading>
              {(event.id > 1 || event.id < now.id) && (
                <EventPager>
                  {event.id > 1 && (
                    <PagerButton>
                      <ButtonLink
                        to={`/dream-team/${event.id - 1}`}
                        variant="secondary"
                      >
                        <ControlArrowLeft />
                        {t("dreamTeam.prev", "Previous")}
                      </ButtonLink>
                    </PagerButton>
                  )}
                  {event.id < now.id && (
                    <PagerButtonNext>
                      <ButtonLink
                        to={`/dream-team/${event.id + 1}`}
                        variant="secondary"
                      >
                        {t("dreamTeam.next", "Next")}
                        <ControlArrowRight />
                      </ButtonLink>
                    </PagerButtonNext>
                  )}
                </EventPager>
              )}
            </Box>
          ) : (
            <Box mx={2}>
              <SubHeading>{t("dreamTeam.overall", "Overall")}</SubHeading>
            </Box>
          )}
          {/* SCOREBOARD */}

          <Box>
            <DTScoreboard>
              <DTScoreboardPanel>
                <DTScoreboardHeading>
                  {t("dreamTeam.topPlayer", "Top Player")}
                </DTScoreboardHeading>
                <Box mb={1}>
                  <BoldLinkButton
                    onClick={() => showElementDialog(topPlayer.id)}
                  >
                    {topPlayer.web_name}
                    <ControlArrowRight />
                  </BoldLinkButton>
                </Box>
                <Box mb={1}>
                  <TopPlayer>
                    <Media
                      img={
                        <ElementShirt
                          elementId={topPlayer.id}
                          sizes="(min-width: 1024px) 55px, (min-width: 610px) 44px, 33px"
                        />
                      }
                      centred={true}
                    >
                      <TopPlayerData>
                        <div>{teamsById[topPlayer.team].short_name}</div>
                        <div>
                          {data.top_player.points}{" "}
                          {t("dreamTeam.points", "Points")}
                        </div>
                      </TopPlayerData>
                    </Media>
                  </TopPlayer>
                </Box>
              </DTScoreboardPanel>
              <DTScoreboardPanel>
                <DTScoreboardHeading>
                  {t("dreamTeam.totalPoints", "Total Points")}
                </DTScoreboardHeading>
                <DTScoreboardValue>
                  {data.team.reduce((total, e) => total + e.points, 0)}
                </DTScoreboardValue>
                {event ? (
                  <BoldLink to="/dream-team/">
                    {t("dreamTeam.overall", "Overall")}
                  </BoldLink>
                ) : (
                  <BoldLink to={`/dream-team/${now.id}`}>
                    {t("dreamTeam.round", "Round")}
                  </BoldLink>
                )}
              </DTScoreboardPanel>
            </DTScoreboard>
          </Box>
        </DTScoreboardWrapper>
        {/* TEAM */}
        <PitchWrapper>
          <Box mx={2} mb={2}>
            <Tabs centered>
              <TabPanel
                label={t("dreamTeam.pitchView", "Pitch View")}
                link="pitch"
              >
                <Box mx={-2} pt={4}>
                  <PitchFormation
                    chipName={""}
                    formation={formation}
                    picks={picks}
                    renderDreamTeam={() => null}
                    renderElementMenu={handleShowMenuForElement}
                    renderPickValue={renderPickValue}
                  />
                </Box>
              </TabPanel>
              <TabPanel
                label={t("dreamTeam.listView", "List View")}
                link="list"
              >
                <DreamTeamTableWrap>
                  <DreamTeamTable
                    elements={data.team.map((e) => e.element)}
                    renderElementMenu={handleShowMenuForElement}
                    dataById={elementsDataById}
                  />
                </DreamTeamTableWrap>
              </TabPanel>
            </Tabs>
          </Box>
        </PitchWrapper>
        {event && elementForMenu ? (
          <ElementExplainDialog
            elementId={elementForMenu}
            eventId={event.id}
            closeDialog={handleHideMenuForElement}
          />
        ) : null}
        {event && <Fixtures eventId={event.id} />}
      </Main>
      <Secondary>
        <span>&nbsp;</span>
      </Secondary>
    </Wrapper>
  );
};

export { DreamTeam as DreamTeamTest };

export default withTranslation()(DreamTeam);
